import { Bot } from 'lucide-react';
import type { ReactNode } from 'react';
import { POSITION_STATUS_LABELS, type PositionStatus } from '@nest-recruit/shared';
import { formatDateTime, orEmpty } from '../../../lib/format';
import type { PositionHistoryEntry } from '../api/organisation-api';
import styles from './PositionHistory.module.css';

/**
 * ORG-03's history tab — the event log, read back.
 *
 * There is no separate change table. Every line here is the same row a metric is
 * computed from (non-negotiable #1 and #10), which is why the history and the
 * numbers can never disagree.
 *
 * A system actor is marked as one, everywhere (06-qol §12): a seat that turned
 * vacant because somebody resigned must be distinguishable at a glance from one an
 * HR Lead changed by hand.
 */

const EVENT_LABELS: Readonly<Record<string, string>> = {
  'position.created': 'Created',
  'position.updated': 'Details changed',
  'position.status_changed': 'Status changed',
  'position.frozen': 'Frozen',
  'position.unfrozen': 'Unfrozen',
  'position.planned': 'Marked planned',
  'position.reassigned': 'Moved to another unit',
  'position.archived': 'Archived',
  'position.restored': 'Restored',
};

export function PositionHistory({
  entries,
}: {
  readonly entries: readonly PositionHistoryEntry[];
}): ReactNode {
  if (entries.length === 0) {
    return <p className={styles.none}>Nothing has happened to this seat yet.</p>;
  }

  return (
    <ol className={styles.list}>
      {entries.map((entry, index) => (
        <li key={`${entry.occurredAt}-${index}`} className={styles.entry}>
          <div className={styles.line}>
            <span className={styles.what}>{EVENT_LABELS[entry.eventType] ?? entry.eventType}</span>
            <span className={styles.when}>{formatDateTime(entry.occurredAt)}</span>
          </div>

          <div className={styles.line}>
            <span className={styles.who}>
              {entry.actorType === 'system' ? (
                <>
                  <Bot size={13} aria-hidden /> System
                </>
              ) : (
                orEmpty(entry.actorName)
              )}
            </span>
            <Detail entry={entry} />
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * What actually changed, in words.
 *
 * Only the fields somebody would ask about. Dumping the whole payload would be
 * honest and unreadable, and the raw event is one query away for anyone who needs
 * it.
 */
function Detail({ entry }: { readonly entry: PositionHistoryEntry }): ReactNode {
  const after = entry.after ?? {};
  const before = entry.before ?? {};

  const from = statusOf(before);
  const to = statusOf(after);
  if (from && to) {
    return (
      <span className={styles.detail}>
        {POSITION_STATUS_LABELS[from]} → {POSITION_STATUS_LABELS[to]}
        {typeof after.reason === 'string' && after.reason !== '' ? ` · ${after.reason}` : ''}
      </span>
    );
  }

  if (typeof after.bandChangeReason === 'string') {
    return <span className={styles.detail}>Salary band · {after.bandChangeReason}</span>;
  }

  if (typeof after.note === 'string' && after.note !== '') {
    return <span className={styles.detail}>{after.note}</span>;
  }

  if (to) return <span className={styles.detail}>{POSITION_STATUS_LABELS[to]}</span>;
  return null;
}

function statusOf(payload: Record<string, unknown>): PositionStatus | null {
  const value = payload.status;
  return typeof value === 'string' && value in POSITION_STATUS_LABELS
    ? (value as PositionStatus)
    : null;
}
